import { IonButton, IonContent, IonPage, IonSpinner } from '@ionic/react';
import { type ReactNode, useState } from 'react';
import { Redirect } from 'react-router-dom';

import type { Company } from '~/api/auth';
import {
  type CompanyInvitation,
  companyTeamRefusalOf,
  invitationBouncedNotice,
  isInvitationBounced,
} from '~/api/companyUsers';
import { useCompany } from '~/api/hooks/useCompany';
import { useCreateCompanyInvitation } from '~/api/hooks/useCompanyUsers';
import { AccessLevelsPicker } from '~/components/AccessLevelsPicker';
import { Alert } from '~/components/Alert';
import { CompanyRolePicker } from '~/components/CompanyRolePicker';
import { FieldWrap, MaskedInput } from '~/components/forms/FormField';
import { Icon } from '~/components/ui/Icon';
import { useAppNavigation } from '~/hooks/useAppNavigation';
import { useNetworkStatus } from '~/hooks/useNetworkStatus';
import { ROUTES } from '~/routes';
import {
  type CompanyInvitationFieldErrors,
  EMPTY_COMPANY_INVITATION_FORM,
  accessLevelFieldErrors,
  checkCompanyInvitation,
  companyInvitationFieldErrors,
} from '~/schemas/companyUser';
import type { AccessLevel, CompanyRole, FiscalModuleKey } from '~/session/access';

const EMAIL_FAILED_NOTICE = 'Convite criado, mas o e-mail não saiu; use Reenviar mais tarde';
const OFFLINE_NOTICE = 'Sem conexão. Conecte-se para enviar o convite.';
const FIELD_LABEL_CLASS = 'font-mono text-[11px] uppercase tracking-overline text-ink-muted';

type LevelErrors = Partial<Record<FiscalModuleKey, string>>;

/** O convite criado que não chegou: o endereço devolvido pede revogar; a falha de envio, reenviar. */
function unsentNoticeOf(invitation: CompanyInvitation): string {
  return isInvitationBounced(invitation)
    ? invitationBouncedNotice(invitation.email)
    : EMAIL_FAILED_NOTICE;
}

export function ConvidarUsuario(): JSX.Element {
  const { selected } = useCompany();

  return (
    <IonPage>
      <IonContent fullscreen>
        <div className="rx-container rx-screen px-6 pb-8 pt-3">
          {selected ? <InviteForm company={selected} /> : <Redirect to={ROUTES.dashboard} />}
        </div>
      </IonContent>
    </IonPage>
  );
}

function InviteForm({ company }: { company: Company }): JSX.Element {
  const nav = useAppNavigation();
  const { isOnline } = useNetworkStatus();
  const create = useCreateCompanyInvitation(company.id);

  const [form, setForm] = useState(EMPTY_COMPANY_INVITATION_FORM);
  const [fieldErrors, setFieldErrors] = useState<CompanyInvitationFieldErrors>({});
  const [levelErrors, setLevelErrors] = useState<LevelErrors>({});
  const [refusalMessage, setRefusalMessage] = useState<string | null>(null);
  const [unsentNotice, setUnsentNotice] = useState<string | null>(null);

  const goToTeam = (): void => nav.replace(ROUTES.empresaUsuarios);

  const changeLevel = (module: FiscalModuleKey, level: AccessLevel): void => {
    setForm((current) => ({ ...current, access: { ...current.access, [module]: level } }));
  };

  const changeRole = (role: CompanyRole): void => {
    setForm((current) => ({ ...current, role }));
  };

  const send = (): void => {
    if (!isOnline) return;
    const { payload, errors } = checkCompanyInvitation(form);
    setFieldErrors(errors);
    if (!payload) return;
    setRefusalMessage(null);
    setLevelErrors({});
    create.mutate(payload, {
      onSuccess: (invitation) => {
        if (invitation.email_sent) {
          goToTeam();
          return;
        }
        setUnsentNotice(unsentNoticeOf(invitation));
      },
      onError: (error) => {
        const refusal = companyTeamRefusalOf(error);
        setRefusalMessage(refusal.message || null);
        setFieldErrors(companyInvitationFieldErrors(refusal.fieldErrors));
        setLevelErrors(accessLevelFieldErrors(refusal.fieldErrors));
      },
    });
  };

  if (unsentNotice) {
    return (
      <InviteScreen onBack={goToTeam}>
        <div className="mt-6">
          <Alert message={unsentNotice} />
          <IonButton expand="block" fill="outline" className="mt-4" onClick={goToTeam}>
            Ver a lista
          </IonButton>
        </div>
      </InviteScreen>
    );
  }

  return (
    <InviteScreen onBack={() => nav.back()}>
      <div className="mt-6 flex flex-col gap-6">
        <FieldWrap
          id="convidar-email"
          label="E-mail"
          error={fieldErrors.email}
          hint="A pessoa recebe um link para criar a própria senha."
        >
          <MaskedInput
            id="convidar-email"
            type="email"
            inputmode="email"
            autocomplete="off"
            value={form.email}
            onChange={(value) => setForm((current) => ({ ...current, email: value }))}
            placeholder="pessoa@empresa.com.br"
          />
        </FieldWrap>

        <CompanyRolePicker value={form.role} onChange={changeRole} error={fieldErrors.role} />

        <div>
          <p className={FIELD_LABEL_CLASS}>Acesso por módulo</p>
          <div className="mt-3">
            <AccessLevelsPicker
              value={form.access}
              ceiling={company.access}
              onChange={changeLevel}
              errors={levelErrors}
            />
          </div>
        </div>
      </div>

      {refusalMessage && (
        <div className="mt-6">
          <Alert message={refusalMessage} />
        </div>
      )}

      <IonButton
        expand="block"
        className="rx-button mt-6"
        disabled={create.isPending || !isOnline}
        onClick={send}
      >
        {create.isPending ? <IonSpinner name="dots" /> : 'Enviar convite'}
      </IonButton>

      {!isOnline && <p className="mt-2 text-[12px] text-ink-subtle">{OFFLINE_NOTICE}</p>}
    </InviteScreen>
  );
}

interface InviteScreenProps {
  onBack(): void;
  children: ReactNode;
}

function InviteScreen({ onBack, children }: InviteScreenProps): JSX.Element {
  return (
    <>
      <button type="button" className="rx-iconbtn" onClick={onBack} aria-label="Voltar">
        <Icon name="back" size={18} />
      </button>
      <h1 className="mt-3.5 text-[30px] font-medium tracking-display-tight text-ink">
        Convidar usuário
      </h1>
      <p className="mt-1 text-[13px] text-ink-muted">
        O convite vai por e-mail; quem recebe cria a própria senha.
      </p>
      {children}
    </>
  );
}
